// New Request form (Story #4613, Stream 8a) — mockup 01. Type + priority +
// subject (8–120) + description (30–5000) + attachments (immediate stage/upload
// via useSupportAttachments) + diagnostic-context chips + advisory duplicate hint
// + localStorage draft autosave. Attachments stage against a lazily-created
// ticket (the backend stage endpoint requires an existing ticket), so the first
// attachment creates the ticket once the subject/description are valid.

import { useEffect, useMemo, useRef, useState } from 'react'
import {
  DESCRIPTION_MAX,
  DESCRIPTION_MIN,
  PRIORITY_OPTIONS,
  REQUEST_TYPE_OPTIONS,
  SUBJECT_MAX,
  SUBJECT_MIN,
  SUPPORT_PORTAL,
  backendRequestType,
} from '../../constants/support'
import type { Priority, SupportTicket, UiRequestType } from '../../types/support'
import { createTicket } from '../../services/support'
import { collectDiagnostics } from '../../utils/supportDiagnostics'
import { useSupportAttachments } from '../../hooks/useSupportAttachments'
import { useSupportDraft, type SupportDraft } from '../../hooks/useSupportDraft'
import AttachmentChip from './AttachmentChip'
import AttachmentDropzone from './AttachmentDropzone'
import DuplicateHint from './DuplicateHint'

function subjectError(s: string): string | null {
  const t = s.trim()
  if (t.length < SUBJECT_MIN) return `Subject must be at least ${SUBJECT_MIN} characters`
  if (t.length > SUBJECT_MAX) return `Subject must be ${SUBJECT_MAX} characters or fewer`
  return null
}
function descriptionError(s: string): string | null {
  const t = s.trim()
  if (t.length < DESCRIPTION_MIN) return `Description must be at least ${DESCRIPTION_MIN} characters`
  if (t.length > DESCRIPTION_MAX) return `Description must be ${DESCRIPTION_MAX} characters or fewer`
  return null
}

export interface NewRequestPrefill {
  requestType?: UiRequestType
  subject?: string
  description?: string
}

interface NewRequestFormProps {
  userId: string | undefined
  myTickets: SupportTicket[]
  onSubmitted: (ticket: SupportTicket) => void
  onCancel: () => void
  onOpenTicket: (ticketId: string) => void
  /** Optional prefill (error-report fallback path, Stream 8b). */
  prefill?: NewRequestPrefill
}

export default function NewRequestForm({
  userId,
  myTickets,
  onSubmitted,
  onCancel,
  onOpenTicket,
  prefill,
}: NewRequestFormProps) {
  const { initialDraft, save, clear } = useSupportDraft(userId)
  const seed: SupportDraft | null = prefill
    ? {
        requestType: prefill.requestType ?? 'bug',
        priority: 'normal',
        subject: prefill.subject ?? '',
        description: prefill.description ?? '',
        includeDiagnostics: true,
        attachments: [],
      }
    : initialDraft

  const [requestType, setRequestType] = useState<UiRequestType>(seed?.requestType ?? 'bug')
  const [priority, setPriority] = useState<Priority>(seed?.priority ?? 'normal')
  const [subject, setSubject] = useState(seed?.subject ?? '')
  const [description, setDescription] = useState(seed?.description ?? '')
  const [includeDiagnostics, setIncludeDiagnostics] = useState(seed?.includeDiagnostics ?? true)
  const [showErrors, setShowErrors] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)

  const diagnostics = useMemo(() => collectDiagnostics(), [])
  const idemRef = useRef<string>(`idem-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`)
  const createdTicketRef = useRef<SupportTicket | null>(null)
  const createInFlightRef = useRef<Promise<string> | null>(null)

  // Latest form values for the async ensureTicketId (avoids stale closures).
  const formStateRef = useRef({ requestType, priority, subject, description, includeDiagnostics })
  formStateRef.current = { requestType, priority, subject, description, includeDiagnostics }

  async function ensureTicketId(): Promise<string> {
    if (createdTicketRef.current) return createdTicketRef.current.ticket_id
    if (createInFlightRef.current) return createInFlightRef.current
    const st = formStateRef.current
    if (subjectError(st.subject) || descriptionError(st.description)) {
      setShowErrors(true)
      throw new Error('Add a subject and description first, then attach files.')
    }
    const p = (async () => {
      const ticket = await createTicket({
        subject: st.subject.trim(),
        description: st.description.trim(),
        request_type: backendRequestType(st.requestType),
        portal: SUPPORT_PORTAL,
        idempotency_key: idemRef.current,
        priority: st.priority,
        diagnostic_context: st.includeDiagnostics ? diagnostics : undefined,
      })
      // Keep the UI-only priority for the confirmation navigation.
      createdTicketRef.current = { ...ticket, priority: st.priority }
      return ticket.ticket_id
    })()
    createInFlightRef.current = p
    try {
      return await p
    } finally {
      createInFlightRef.current = null
    }
  }

  const attach = useSupportAttachments({ ensureTicketId })

  // Restore draft attachment chips (icon-only) once.
  const restoredRef = useRef(false)
  useEffect(() => {
    if (restoredRef.current) return
    restoredRef.current = true
    if (seed?.attachments?.length) attach.restore(seed.attachments)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Autosave draft on change (never persists file bytes — only chip metadata).
  useEffect(() => {
    if (createdTicketRef.current) return // don't autosave once the ticket is created
    save({
      requestType,
      priority,
      subject,
      description,
      includeDiagnostics,
      attachments: attach.attachments
        .filter((a) => a.status !== 'error')
        .map((a) => ({
          filename: a.filename,
          sizeBytes: a.sizeBytes,
          contentType: a.contentType,
          isVideo: a.isVideo,
        })),
    })
  }, [requestType, priority, subject, description, includeDiagnostics, attach.attachments, save])

  const sErr = subjectError(subject)
  const dErr = descriptionError(description)

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setSubmitError(null)
    if (sErr || dErr) {
      setShowErrors(true)
      return
    }
    setSubmitting(true)
    try {
      await ensureTicketId()
      const ticket = createdTicketRef.current
      if (!ticket) throw new Error('Could not create the ticket — please try again.')
      clear()
      onSubmitted(ticket)
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Could not submit — please try again.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <form onSubmit={handleSubmit} className="text-slate-800">
      {/* Type */}
      <label className="mb-1.5 mt-3 block text-[12.5px] font-semibold text-iflow-900">
        What do you need help with? <span className="text-rose-600">*</span>
      </label>
      <div className="inline-flex overflow-hidden rounded-md border border-slate-300">
        {REQUEST_TYPE_OPTIONS.map((opt) => (
          <button
            key={opt.value}
            type="button"
            onClick={() => setRequestType(opt.value)}
            className={`border-r border-slate-200 px-3.5 py-2 text-[13px] last:border-r-0 ${
              requestType === opt.value ? 'bg-iflow-500 font-semibold text-white' : 'bg-white text-slate-700 hover:bg-slate-50'
            }`}
          >
            {opt.label}
          </button>
        ))}
      </div>

      {/* Priority */}
      <label className="mb-1.5 mt-3 block text-[12.5px] font-semibold text-iflow-900">
        Priority <span className="text-rose-600">*</span>{' '}
        <span className="font-normal text-slate-500">— Urgent = you are blocked from working</span>
      </label>
      <select
        value={priority}
        onChange={(e) => setPriority(e.target.value as Priority)}
        className="w-full rounded-md border border-slate-300 bg-white px-2.5 py-2 text-[13px] text-slate-900"
      >
        {PRIORITY_OPTIONS.map((p) => (
          <option key={p.value} value={p.value}>
            {p.label}
          </option>
        ))}
      </select>

      {/* Subject */}
      <label className="mb-1.5 mt-3 block text-[12.5px] font-semibold text-iflow-900">
        Subject <span className="text-rose-600">*</span>
        <span className={`float-right text-[11px] font-normal ${subject.trim().length > SUBJECT_MAX ? 'text-rose-600' : 'text-slate-500'}`}>
          {subject.trim().length} / {SUBJECT_MAX}
        </span>
      </label>
      <input
        type="text"
        value={subject}
        onChange={(e) => setSubject(e.target.value)}
        onBlur={() => setShowErrors(true)}
        placeholder="Short summary of the problem"
        className="w-full rounded-md border border-slate-300 bg-white px-2.5 py-2 text-[13px] text-slate-900"
      />
      {showErrors && sErr && <p className="mt-1 text-[11.5px] text-rose-600">{sErr}</p>}
      <DuplicateHint subject={subject} tickets={myTickets} onOpenTicket={onOpenTicket} />

      {/* Description */}
      <label className="mb-1.5 mt-3 block text-[12.5px] font-semibold text-iflow-900">
        Description <span className="text-rose-600">*</span>
        <span className={`float-right text-[11px] font-normal ${description.trim().length > DESCRIPTION_MAX ? 'text-rose-600' : 'text-slate-500'}`}>
          {description.trim().length} / {DESCRIPTION_MAX}
        </span>
      </label>
      <textarea
        rows={5}
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        onBlur={() => setShowErrors(true)}
        placeholder="Steps to reproduce, what you expected, and what actually happened"
        className="w-full resize-y rounded-md border border-slate-300 bg-white px-2.5 py-2 text-[13px] text-slate-900"
      />
      {showErrors && dErr && <p className="mt-1 text-[11.5px] text-rose-600">{dErr}</p>}

      {/* Attachments */}
      <label className="mb-1.5 mt-3 block text-[12.5px] font-semibold text-iflow-900">
        Attachments <span className="font-normal text-slate-500">— up to 5 files · images/docs 10 MB · one video 50 MB</span>
      </label>
      <AttachmentDropzone
        onFiles={attach.addFiles}
        fileCount={attach.fileCount}
        hasVideo={attach.hasVideo}
      />
      {attach.attachments.map((a) => (
        <AttachmentChip key={a.localId} attachment={a} onRemove={attach.remove} onRetry={attach.retry} />
      ))}

      {/* Diagnostic context */}
      <label className="mb-1.5 mt-3 block text-[12.5px] font-semibold text-iflow-900">
        Diagnostic context <span className="font-normal text-slate-500">— sent with your request</span>
      </label>
      <div className="flex flex-wrap gap-1.5">
        <span className="rounded-full bg-iflow-100 px-2.5 py-0.5 text-[11.5px] text-iflow-800">Portal: {diagnostics.portal}</span>
        <span className="rounded-full bg-iflow-100 px-2.5 py-0.5 text-[11.5px] text-iflow-800">Page: {diagnostics.page}</span>
        <span className="rounded-full bg-iflow-100 px-2.5 py-0.5 text-[11.5px] text-iflow-800">Version: {diagnostics.version}</span>
        <span className="rounded-full bg-iflow-100 px-2.5 py-0.5 text-[11.5px] text-iflow-800">{diagnostics.browser}</span>
      </div>
      <label className="mt-1.5 flex items-center gap-2 text-[12px] text-slate-700">
        <input type="checkbox" checked={includeDiagnostics} onChange={(e) => setIncludeDiagnostics(e.target.checked)} />
        Include diagnostic context
      </label>

      {submitError && (
        <p role="alert" className="mt-3 rounded-lg bg-rose-50 px-3 py-2 text-[12.5px] text-rose-700">
          {submitError}
        </p>
      )}

      {/* Actions */}
      <div className="mt-4 flex items-center gap-2.5">
        <button
          type="submit"
          disabled={submitting || attach.uploading}
          className="rounded-lg bg-amber-400 px-4 py-2.5 text-[13.5px] font-semibold text-gray-900 hover:bg-amber-300 disabled:opacity-60"
        >
          {submitting ? 'Submitting…' : 'Submit request'}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="rounded-lg border border-iflow-200 bg-transparent px-4 py-2.5 text-[13.5px] font-semibold text-iflow-700 hover:bg-iflow-50"
        >
          Cancel
        </button>
        <span className="text-[12px] text-slate-500">
          {attach.uploading ? 'Finishing uploads…' : 'Draft saved automatically'}
        </span>
      </div>
    </form>
  )
}
